import React, { useMemo, useRef } from 'react';
import { Calendar, ChevronRight, Star, Flag, Clock, ChevronLeft } from 'lucide-react';
import { theme } from '../../theme';

const ProductionTimeline = ({ 
  viewMode, 
  departmentsToUse = [],
  upcomingMilestones = [],
  currentProject 
}) => {
  // Reference to the scrollable container
  const timelineScrollRef = useRef(null);
  
  // Handle scroll buttons
  const handleScroll = (direction) => {
    if (!timelineScrollRef.current) return;
    
    const scrollAmount = 300; // Adjust scroll amount as needed
    const currentScroll = timelineScrollRef.current.scrollLeft;
    
    timelineScrollRef.current.scrollTo({
      left: direction === 'left' ? currentScroll - scrollAmount : currentScroll + scrollAmount,
      behavior: 'smooth'
    });
  };
  
  // Other functions remain the same...
  const calculateTimeProgress = () => {
    if (!currentProject?.dueDate) return 35; // Fallback value
    
    const startDate = new Date('2024-12-01'); // Start from previous December for better context
    const dueDate = new Date(currentProject.dueDate);
    const today = new Date();
    
    const totalDuration = dueDate.getTime() - startDate.getTime();
    const elapsedDuration = today.getTime() - startDate.getTime();
    
    const progress = Math.max(0, Math.min(100, (elapsedDuration / totalDuration) * 100));
    return progress;
  };
  
  // Generate time period text based on project due date
  const generateTimePeriodText = () => {
    if (!currentProject?.dueDate) return 'Spring-Summer 2025'; // Default fallback
    
    const dueDate = new Date(currentProject.dueDate);
    const year = dueDate.getFullYear();
    
    // Determine season based on month
    const month = dueDate.getMonth();
    let season;
    
    if (month >= 2 && month <= 4) season = 'Spring';
    else if (month >= 5 && month <= 7) season = 'Summer';
    else if (month >= 8 && month <= 10) season = 'Fall';
    else season = 'Winter';
    
    return `${season} ${year}`;
  };
  
  // Phase mapping and progress calculation (same as before)
  const phases = useMemo(() => {
    // Make sure departmentsToUse exists before proceeding
    if (!departmentsToUse || !Array.isArray(departmentsToUse)) {
      // Return default phases if no departments data
      return [
        { name: 'Pre-Production', color: 'bg-green-100 border-green-200 text-green-800', progress: 0, isCurrentPhase: false },
        { name: 'Modeling & Rigging', color: 'bg-yellow-50 border-yellow-100 text-yellow-800', progress: 0, isCurrentPhase: false },
        { name: 'Animation', color: 'bg-blue-50 border-blue-100 text-blue-800', progress: 0, isCurrentPhase: false },
        { name: 'Lighting', color: 'bg-purple-50 border-purple-100 text-purple-800', progress: 0, isCurrentPhase: false },
        { name: 'Post-Production', color: 'bg-red-50 border-red-100 text-red-800', progress: 0, isCurrentPhase: false }
      ];
    }
    
    const phaseMapping = {
      'Pre-Production': ['Pre-Production'],
      'Modeling & Rigging': ['Modeling', 'Rigging'],
      'Animation': ['Animation'],
      'Lighting': ['Lighting & Rendering'],
      'Post-Production': ['Post-Production']
    };
    
    const phaseColors = {
      'Pre-Production': 'bg-green-100 border-green-200 text-green-800',
      'Modeling & Rigging': 'bg-yellow-50 border-yellow-100 text-yellow-800',
      'Animation': 'bg-blue-50 border-blue-100 text-blue-800',
      'Lighting': 'bg-purple-50 border-purple-100 text-purple-800',
      'Post-Production': 'bg-red-50 border-red-100 text-red-800'
    };
    
    return Object.entries(phaseMapping).map(([phaseName, deptNames]) => {
      const phaseDepts = departmentsToUse.filter(dept => 
        dept && dept.name && deptNames.includes(dept.name)
      );
      
      let taskCount = 0;
      let progressSum = 0;
      
      phaseDepts.forEach(dept => {
        if (dept && dept.tasks && Array.isArray(dept.tasks)) {
          dept.tasks.forEach(task => {
            if (task) {
              taskCount++;
              progressSum += task.progress || 0;
            }
          });
        }
      });
      
      const progress = taskCount > 0 ? Math.round(progressSum / taskCount) : 0;
      
      const isCurrentPhase = phaseDepts.some(dept => 
        dept && dept.tasks && Array.isArray(dept.tasks) && 
        dept.tasks.some(task => task && task.status === 'in-progress')
      );
      
      return {
        name: phaseName,
        color: phaseColors[phaseName],
        progress,
        isCurrentPhase
      };
    });
  }, [departmentsToUse]);
  
  // Generate months for timeline
  const generateMonths = () => {
    const startDate = new Date('2025-02-01'); // Start from previous December for better context
    
    const endDate = currentProject?.dueDate 
      ? new Date(currentProject.dueDate) 
      : new Date('2025-09-30'); // Default if no due date
    
    // Add 2 months after due date to show context after completion
    const extendedEndDate = new Date(endDate);
    extendedEndDate.setMonth(extendedEndDate.getMonth() + 2);
    
    const months = [];
    const currentDate = new Date(startDate);
    
    while (currentDate <= extendedEndDate) {
      months.push(new Date(currentDate).toLocaleDateString('en-US', { month: 'short' }));
      currentDate.setMonth(currentDate.getMonth() + 1);
    }
    
    return months;
  };
  
  const months = generateMonths();
  const timeProgress = calculateTimeProgress();
  const timePeriodText = generateTimePeriodText();
  
  // Calculate the minimum width needed for the timeline based on the number of months
  const timelineWidth = Math.max(months.length * 80, 100); // 80px per month, minimum 100px
  
  return (
    <div className="mt-8 bg-white rounded-lg shadow-sm border border-gray-100 transition-all duration-300 hover:shadow-md overflow-hidden">
      <div className="p-4 md:p-6 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Calendar size={viewMode === 'tv' ? 24 : 20} className="mr-2 text-green-600" />
            <h2 className="font-medium text-gray-800 text-lg">Production Timeline</h2>
          </div>
          <div className="flex items-center">
            <span className="text-xs text-gray-500 mr-3">{timePeriodText}</span>
            
            {currentProject && (
              <span className="text-xs font-medium bg-green-50 text-green-700 px-2 py-1 rounded-full">
                Due: {new Date(currentProject.dueDate).toLocaleDateString('en-US', { 
                  month: 'short', 
                  day: 'numeric',
                  year: 'numeric'
                })}
              </span>
            )}
          </div>
        </div>
      </div>
      
      <div className="p-4 md:p-6">
        {/* Upcoming Milestones - Only show if they exist */}
        {upcomingMilestones && upcomingMilestones.length > 0 && (
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
              <Star size={16} className="mr-2 text-amber-500" />
              Upcoming Milestones
            </h3>
            <div className="space-y-2">
              {upcomingMilestones.map((milestone, idx) => (
                <div key={idx} className="flex items-center justify-between bg-amber-50 px-4 py-3 rounded-lg border border-amber-100">
                  <div className="flex items-center">
                    <Flag size={16} className="mr-3 text-amber-500" />
                    <span className="font-medium text-gray-800">{milestone.name}</span>
                  </div>
                  <div className="flex items-center text-sm">
                    <Clock size={14} className="mr-1 text-gray-500" />
                    <span className="text-gray-600">
                      {milestone.days === 0 ? 'Today' : 
                       milestone.days === 1 ? 'Tomorrow' : 
                       `${milestone.days} days`}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Timeline visualization with scroll controls */}
        <div className="relative mb-8">
          {/* Scroll controls */}
          <div className="flex justify-between absolute -top-8 right-0 space-x-2">
            <button 
              onClick={() => handleScroll('left')}
              className="p-1 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
              aria-label="Scroll left"
            >
              <ChevronLeft size={16} className="text-gray-600" />
            </button>
            <button 
              onClick={() => handleScroll('right')}
              className="p-1 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
              aria-label="Scroll right"
            >
              <ChevronRight size={16} className="text-gray-600" />
            </button>
          </div>
          
          {/* Scrollable Timeline container */}
          <div 
            ref={timelineScrollRef}
            className="overflow-x-auto hide-scrollbar pb-2"
          >
            <div style={{ minWidth: `${timelineWidth}px` }}>
              {/* Timeline bar */}
              <div className="h-2 bg-gray-100 rounded-full mb-2 relative">
                {/* Progress indicator */}
                <div 
                  className="absolute h-2 bg-green-500 rounded-full" 
                  style={{ width: `${timeProgress}%` }}
                ></div>
                
                {/* Today marker */}
                <div className={`absolute top-0 h-2 w-1 bg-red-500`} style={{ left: `${timeProgress}%` }}>
                  <div className="absolute -top-6 -left-8 bg-red-500 text-white text-xs px-2 py-1 rounded shadow-sm">
                    Today
                  </div>
                </div>
                
                {/* Due date marker */}
                {currentProject?.dueDate && (
                  <div className="absolute top-0 h-2 w-1 bg-green-600" style={{ left: '100%' }}>
                    <div className="absolute -top-6 -left-8 bg-green-600 text-white text-xs px-2 py-1 rounded shadow-sm whitespace-nowrap">
                      Due Date
                    </div>
                  </div>
                )}
                
                {/* Milestone markers */}
                {upcomingMilestones && upcomingMilestones.length > 0 && upcomingMilestones.map((milestone, idx) => {
                  if (!milestone || !milestone.date) return null;
                  
                  const milestoneDate = new Date(milestone.date);
                  const startDate = new Date('2024-12-01');
                  const endDate = currentProject?.dueDate 
                    ? new Date(currentProject.dueDate) 
                    : new Date('2025-09-30');
                    
                  const totalDuration = endDate.getTime() - startDate.getTime();
                  const milestoneDuration = milestoneDate.getTime() - startDate.getTime();
                  
                  const position = Math.max(0, Math.min(100, (milestoneDuration / totalDuration) * 100));
                  
                  return (
                    <div 
                      key={idx} 
                      className="absolute top-0 h-2 w-1 bg-amber-500"
                      style={{ left: `${position}%` }}
                    >
                      <div className="absolute -bottom-6 transform -translate-x-1/2 bg-amber-100 border border-amber-300 text-amber-800 text-xs px-2 py-1 rounded whitespace-nowrap">
                        {milestone.name}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Timeline months */}
              <div className="flex justify-between text-xs text-gray-500 mt-6">
                {months.map((month, idx) => (
                  <div key={idx}>{month}</div>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        {/* Production phases */}
        <div className="space-y-3 mt-6">
          {phases.map((phase, index) => (
            <div key={index} className="flex items-center">
              <div className={`w-36 sm:w-48 font-medium text-sm ${phase.progress === 0 ? 'text-gray-400' : 'text-gray-800'}`}>
                {phase.name}
              </div>
              <div className="flex-1 relative">
                <div className="h-8 bg-gray-50 rounded-lg border border-gray-100 overflow-hidden">
                  {phase.progress > 0 && (
                    <div 
                      className={`h-full ${phase.color}`}
                      style={{ width: `${phase.progress}%` }}
                    >
                      {phase.progress >= 15 && (
                        <div className="flex items-center h-full pl-3">
                          <span className="text-xs font-medium">{phase.progress}%</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Current phase indicator */}
                {phase.isCurrentPhase && (
                  <div className="absolute -right-2 -top-2 w-5 h-5 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
                    <ChevronRight size={12} className="text-white" />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Add custom scrollbar styling */}
      <style jsx="true">{`
        .hide-scrollbar {
          -ms-overflow-style: none;  /* Internet Explorer and Edge */
          scrollbar-width: none;  /* Firefox */
        }
        
        .hide-scrollbar::-webkit-scrollbar {
          display: none; /* Chrome, Safari, Opera */
        }
      `}</style>
    </div>
  );
};

export default ProductionTimeline;
